import { useFormContext, useWatch } from 'react-hook-form';
import type { FC } from 'react';
import { useMemo } from 'react';
import { Box, styled, Typography } from '@mui/material';
import type { PackEntry } from 'types/shared';
import type { AdSetEntry } from '../api/getAdSets';
import useAdSetBudgetSums from '../hooks/useAdSetBudgetSums';
import { getPartnershipBudgetMultiplier } from '../utils/calculateAdsCount';

const StyledTypography = styled(Typography)(({ theme, variant }) => ({
  color: 'rgba(57, 32, 128, 1)',
  fontSize:
    variant === 'body1'
      ? theme.typography.body1.fontSize
      : theme.typography.caption.fontSize,
  fontWeight: variant === 'body1' ? 500 : 400,
}));

const AVAILABLE_STEPS = [3, 4, 5];

const AdSetBudgetCounter: FC = () => {
  const { control } = useFormContext();

  const currentStep = useWatch({ control, name: 'currentStep' }) as number;
  const selectedAdSets = useWatch({ control, name: 'selectedAdSets' }) as AdSetEntry[];
  const budgets = useWatch({
    control,
    name: 'selectedAdSetsBudgets',
  }) as Record<string, string>;
  const campaignBudgets = useWatch({
    control,
    name: 'selectedCampaignsBudgets',
  }) as Record<string, string>;
  const duplicateCounts = useWatch({
    control,
    name: 'selectedAdSetsDuplicatesCount',
  }) as Record<string, string>;
  const packsWithCreatives = useWatch({
    control,
    name: 'packsWithCreatives',
  }) as PackEntry[];
  const marketingProcess = useWatch({
    control,
    name: 'marketingProcess',
  }) as string;
  const partnershipAdsEnabled = useWatch({
    control,
    name: 'partnershipAdsEnabled',
  }) as boolean;
  const partnershipAdSettings = useWatch({
    control,
    name: 'partnershipAdSettings',
  }) as { partnershipId?: string }[] | undefined;

  const normalizedDuplicateCounts = useMemo(() => {
    if (marketingProcess !== 'scaling_eg') {
      return duplicateCounts;
    }

    return selectedAdSets.reduce<Record<string, string>>((acc, adSet) => {
      acc[adSet.id] = duplicateCounts[adSet.id] ?? '1';
      return acc;
    }, {});
  }, [duplicateCounts, marketingProcess, selectedAdSets]);

  const { totalSum } = useAdSetBudgetSums({
    selectedAdSets,
    budgets,
    campaignBudgets,
    duplicateCounts: normalizedDuplicateCounts,
  });

  const partnerBudgetMultiplier = useMemo(
    () =>
      getPartnershipBudgetMultiplier(
        partnershipAdsEnabled,
        partnershipAdSettings,
      ),
    [partnershipAdsEnabled, partnershipAdSettings],
  );

  const isStepThree = currentStep === 3;
  const isScalingEg = marketingProcess === 'scaling_eg';
  const packMultiplier = isScalingEg ? 1 : packsWithCreatives.length;
  const baseAmount = isStepThree
    ? totalSum
    : totalSum * packMultiplier;
  const displayAmount = baseAmount * partnerBudgetMultiplier;
  const displayLabel = isStepThree ? '/ pack' : 'Total';

  if (!AVAILABLE_STEPS.includes(currentStep)) {
    return null;
  }

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        justifyContent: 'flex-start',
        backgroundColor: theme.palette.info.dark,
        px: 2,
        borderRadius: 1,
      })}
    >
      <StyledTypography variant="body1">${displayAmount}</StyledTypography>
      <StyledTypography variant="caption">{displayLabel}</StyledTypography>
    </Box>
  );
};

export default AdSetBudgetCounter;
